import JSZip from "jszip";
import readXlsxFile from 'read-excel-file';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { SessionInfo } from "@/app/lib/context/UserSessionContext";

export async function parseExcelFromBlob(blob: Blob): Promise<{
  headers: string[];
  dataRows: any[][];
}> {
  const arrayBuffer = await blob.arrayBuffer();
  const workbook = XLSX.read(arrayBuffer, { type: 'array' });

  const sheetName = workbook.SheetNames.find(name => {
    const ws = workbook.Sheets[name];
    const rows = XLSX.utils.sheet_to_json(ws, { header: 1, blankrows: false });
    return rows.length > 1;
  });

  if (!sheetName) throw new Error('No non-empty sheet found in Excel file');

  const worksheet = workbook.Sheets[sheetName];
  const rawRows: any[][] = XLSX.utils.sheet_to_json(worksheet, { header: 1, blankrows: false });

  const cleanRows = rawRows.map(row => {
    let lastIndex = row.length;
    while (lastIndex > 0 && (row[lastIndex - 1] === undefined || row[lastIndex - 1] === '')) {
      lastIndex--;
    }
    return row.slice(0, lastIndex);
  });

  const [headersRaw, ...dataRows] = cleanRows;
  const headers = headersRaw.map(h => String(h).trim());

  return { headers, dataRows };
}

interface DataRow {
  [key: string]: string | number | string[];
}

export async function processFile(file: File, sessionInfo: SessionInfo): Promise<FormData> {
  const extension = file.name.split('.').pop()?.toLowerCase();
  const formData = new FormData();

  if (extension === 'zip') {
    return await processZip2(file);
  } else if (extension === 'csv' || extension === 'xlsx' || extension === 'xls') {
    return await processFlatFile(file, extension, formData, sessionInfo);
  } else {
    throw new Error('Unsupported file format');
  }
}


async function processFlatFile(file: File, ext: string | undefined, formData: FormData, sessionInfo: SessionInfo): Promise<FormData> {
  const [headers, ...rows] = await parseTableFile(file, ext);

  const data: DataRow[] = rows.map(row => {
    const rowData: DataRow = {};
    headers.forEach((header, idx) => {
      const key = String(header).trim();
      const val = row[idx] ?? '';
      if (key === 'images' && typeof val === 'string') {
        rowData[key] = val.split(',').map(url => url.trim());
      } else {
        rowData[key] = val;
      }
    });

    // Rename and remove old fields
    const updatedRow: DataRow = {
      ...rowData,
      productSkuId: rowData['sku'],
      productName: rowData['name'],
      imageUrls: rowData['images'],
      // supplierId: rowData['supplier_id'],
      // supplierId:'f67dcdb5-7806-4063-ae08-18127a83eddf',
      supplierId: sessionInfo?.session?.vendorId || '',
      productTypeName: rowData['product_type'],
    };

    delete updatedRow['sku'];
    delete updatedRow['name'];
    delete updatedRow['images'];
    // delete updatedRow['supplier_id'];
    delete updatedRow['product_type'];

    return updatedRow;
  });

  formData.append('data', JSON.stringify(data));
  return formData;
}


async function parseTableFile(file: JSZip.JSZipObject | File, ext?: string): Promise<any[][]> {
  if (ext === 'csv') {
    //@ts-ignore
    const text = typeof file === 'string' ? file : await file.async?.('text') ?? await file.text();
    const parsed = Papa.parse(text.trim(), { delimiter: '=', skipEmptyLines: true });
    if (parsed.errors.length) throw new Error('CSV parsing error');
    return parsed.data as any[][];
  } else {
    const blob = await (file as any).async?.('blob') ?? file;
    return await readXlsxFile(blob);
  }
}



export async function processZip2(file: File) {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const zip = await JSZip.loadAsync(arrayBuffer);

    let dataFile: JSZip.JSZipObject | null = null;
    const imageFiles: Record<string, JSZip.JSZipObject> = {};

    // Find data file and collect images
    zip.forEach((relativePath, file) => {
      if (relativePath.includes('__MACOSX/') || relativePath.startsWith('._') || file.dir) {
        return;
      }

      const ext = relativePath.split('.').pop()?.toLowerCase();
      if ((ext === 'xlsx' || ext === 'xls' || ext === 'csv') && !dataFile) {
        dataFile = file;
      } else if (['jpg', 'jpeg', 'png', 'gif'].includes(ext || '')) {
        const fileName = relativePath.split('/').pop() || '';
        imageFiles[fileName] = file;
      }
    });

    if (!dataFile) throw new Error('No Excel or CSV file found in ZIP');
    //@ts-ignore
    const ext = dataFile.name.split('.').pop()?.toLowerCase();
    let headers: string[] = [];
    let dataRows: any[][] = [];

    if (ext === 'csv') {
      //const csvText = ""; //await dataFile.async('text');
      //@ts-ignore
      const csvText = await dataFile.async('text');
      const parsed = Papa.parse(csvText.trim(), {
        delimiter: ",",
        skipEmptyLines: true,
        header: false,
      });
      if (parsed.errors.length) throw new Error('Error parsing CSV');
      [headers, ...dataRows] = parsed.data as any[][];
    } else {
      //@ts-ignore
      const rawBlob = await dataFile.async('blob');
      const blob = new Blob([rawBlob], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      ({ headers, dataRows } = await parseExcelFromBlob(blob));
    }

    if (dataRows.length === 0) throw new Error("No data rows found in data file");

    const files: Record<string, [string, Blob, string]> = {};
    const data: Record<string, string>[] = [];

    for (const row of dataRows) {
      const rowData: Record<string, string> = {};
      const imageRef = String(row[1]);

      if (imageRef.startsWith('http') || imageRef.startsWith('https')) {
        rowData['images'] = imageRef;
      } else if (imageFiles[imageRef]) {
        const imageBlob = await imageFiles[imageRef].async('blob');
        files[imageRef] = [imageRef, imageBlob, 'application/octet-stream'];
      } else {
        console.warn(`Image not found in ZIP or invalid URL: ${imageRef}`);
      }

      headers.forEach((header, index) => {
        if (index > 1) {
          const key = String(header).trim();
          const value = row[index] ?? '';
          rowData[key] = value;
        }
      });

      if (Object.keys(rowData).length > 0) {
        data.push(rowData);
      }
    }

    const formData = new FormData();
    Object.entries(files).forEach(([filename, [name, blob]]) => {
      formData.append('files', blob, name);
    });

    data.forEach(row => {
      formData.append('data', JSON.stringify(row));
    });

    return formData;

  } catch (error) {
    console.error('Error processing ZIP:', error);
    throw error;
  }
}
